import React from "react";

interface TreeReport {
    blocked_nodes_count: number;
    nodes_with_blocked_ancestor_count: number;
    orphans_count: number;
    cycles_count: number;
    inviter_blocked_count: number;
    missing_parent_count: number;
    duplicate_ids_in_path_count: number;
    samples: {
        blocked_nodes: number[];
        nodes_with_blocked_ancestor: number[];
        orphans: number[];
        cycles: number[];
        inviter_blocked: number[];
        missing_parent: number[];
        duplicate_ids_in_path: number[];
    };
}

interface TreeUserData {
    user: {
        id: number;
        email?: string;
        surname?: string;
        name?: string;
        is_blocked?: boolean;
        inviter_id?: number;
        created_at?: string;
    };
    path: number[];
    blocked_ancestors: {
        id: number;
        surname?: string;
        name?: string;
        is_blocked: boolean;
    }[];
}

interface TreeIntegrityCheckProps {
    treeReport: TreeReport | null;
    isCheckingTree: boolean;
    isLoadingTreeUser: boolean;
    treeUserData:
        | {
              success: boolean;
              data?: TreeUserData | undefined;
          }
        | null
        | undefined;
    isDetailsOpen: boolean;
    onCheckTree: () => void;
    onOpenDetails: (userId: number) => void;
    onCloseDetails: () => void;
}

export const TreeIntegrityCheck: React.FC<TreeIntegrityCheckProps> = ({
    treeReport,
    isCheckingTree,
    isLoadingTreeUser,
    treeUserData,
    isDetailsOpen,
    onCheckTree,
    onOpenDetails,
    onCloseDetails,
}) => {
    return (
        <>
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                    Проверка структуры рефералов
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Проверить структуру реферального дерева на наличие ошибок,
                    заблокированных пользователей, цикличных ссылок и т.д.
                </p>
                <button
                    onClick={onCheckTree}
                    disabled={isCheckingTree}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-md disabled:opacity-50"
                >
                    {isCheckingTree ? "Проверка..." : "Запустить проверку"}
                </button>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {treeReport && (
                        <div className="col-span-full mt-4 text-sm text-gray-900 dark:text-gray-100">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="p-3 rounded border border-gray-200 dark:border-gray-700">
                                    <div>
                                        Заблокированные узлы:{" "}
                                        {treeReport.blocked_nodes_count}
                                    </div>
                                    <div>
                                        Узлы с заблокированным предком:{" "}
                                        {
                                            treeReport.nodes_with_blocked_ancestor_count
                                        }
                                    </div>
                                    <div>
                                        Орфаны (в дереве нет пользователя):{" "}
                                        {treeReport.orphans_count}
                                    </div>
                                    <div>
                                        Циклы (сам в своём пути):{" "}
                                        {treeReport.cycles_count}
                                    </div>
                                    <div>
                                        Пригласитель заблокирован:{" "}
                                        {treeReport.inviter_blocked_count}
                                    </div>
                                    <div>
                                        Несуществующий ближайший родитель:{" "}
                                        {treeReport.missing_parent_count}
                                    </div>
                                    <div>
                                        Дубликаты ID в path:{" "}
                                        {treeReport.duplicate_ids_in_path_count}
                                    </div>
                                </div>
                                <div className="p-3 rounded border border-gray-200 dark:border-gray-700 overflow-auto">
                                    <div className="font-semibold mb-2">
                                        Примеры (до 50):
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <div className="text-gray-500 dark:text-gray-400">
                                                Заблокированные
                                            </div>
                                            <div className="break-words">
                                                {(
                                                    treeReport.samples
                                                        ?.blocked_nodes || []
                                                ).join(", ") || "-"}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-gray-500 dark:text-gray-400">
                                                С заблок. предком
                                            </div>
                                            <div className="break-words space-x-2">
                                                {(
                                                    treeReport.samples
                                                        ?.nodes_with_blocked_ancestor ||
                                                    []
                                                ).length
                                                    ? (
                                                          treeReport.samples
                                                              ?.nodes_with_blocked_ancestor ||
                                                          []
                                                      ).map(
                                                          (
                                                              id: number,
                                                              idx: number
                                                          ) => (
                                                              <button
                                                                  key={idx}
                                                                  onClick={() =>
                                                                      onOpenDetails(
                                                                          id
                                                                      )
                                                                  }
                                                                  className="text-blue-600 dark:text-blue-400 underline"
                                                              >
                                                                  {id}
                                                              </button>
                                                          )
                                                      )
                                                    : "-"}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-gray-500 dark:text-gray-400">
                                                Орфаны
                                            </div>
                                            <div className="break-words space-x-2">
                                                {(
                                                    treeReport.samples
                                                        ?.orphans || []
                                                ).length
                                                    ? (
                                                          treeReport.samples
                                                              ?.orphans || []
                                                      ).map(
                                                          (
                                                              id: number,
                                                              idx: number
                                                          ) => (
                                                              <button
                                                                  key={idx}
                                                                  onClick={() =>
                                                                      onOpenDetails(
                                                                          id
                                                                      )
                                                                  }
                                                                  className="text-blue-600 dark:text-blue-400 underline"
                                                              >
                                                                  {id}
                                                              </button>
                                                          )
                                                      )
                                                    : "-"}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-gray-500 dark:text-gray-400">
                                                Циклы
                                            </div>
                                            <div className="break-words space-x-2">
                                                {(
                                                    treeReport.samples
                                                        ?.cycles || []
                                                ).length
                                                    ? (
                                                          treeReport.samples
                                                              ?.cycles || []
                                                      ).map(
                                                          (
                                                              id: number,
                                                              idx: number
                                                          ) => (
                                                              <button
                                                                  key={idx}
                                                                  onClick={() =>
                                                                      onOpenDetails(
                                                                          id
                                                                      )
                                                                  }
                                                                  className="text-blue-600 dark:text-blue-400 underline"
                                                              >
                                                                  {id}
                                                              </button>
                                                          )
                                                      )
                                                    : "-"}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-gray-500 dark:text-gray-400">
                                                Пригласитель заблокирован
                                            </div>
                                            <div className="break-words space-x-2">
                                                {(
                                                    treeReport.samples
                                                        ?.inviter_blocked || []
                                                ).length
                                                    ? (
                                                          treeReport.samples
                                                              ?.inviter_blocked ||
                                                          []
                                                      ).map(
                                                          (
                                                              id: number,
                                                              idx: number
                                                          ) => (
                                                              <button
                                                                  key={idx}
                                                                  onClick={() =>
                                                                      onOpenDetails(
                                                                          id
                                                                      )
                                                                  }
                                                                  className="text-blue-600 dark:text-blue-400 underline"
                                                              >
                                                                  {id}
                                                              </button>
                                                          )
                                                      )
                                                    : "-"}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-gray-500 dark:text-gray-400">
                                                Нет родителя
                                            </div>
                                            <div className="break-words space-x-2">
                                                {(
                                                    treeReport.samples
                                                        ?.missing_parent || []
                                                ).length
                                                    ? (
                                                          treeReport.samples
                                                              ?.missing_parent ||
                                                          []
                                                      ).map(
                                                          (
                                                              id: number,
                                                              idx: number
                                                          ) => (
                                                              <button
                                                                  key={idx}
                                                                  onClick={() =>
                                                                      onOpenDetails(
                                                                          id
                                                                      )
                                                                  }
                                                                  className="text-blue-600 dark:text-blue-400 underline"
                                                              >
                                                                  {id}
                                                              </button>
                                                          )
                                                      )
                                                    : "-"}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-gray-500 dark:text-gray-400">
                                                Дубликаты в path
                                            </div>
                                            <div className="break-words space-x-2">
                                                {(
                                                    treeReport.samples
                                                        ?.duplicate_ids_in_path ||
                                                    []
                                                ).length
                                                    ? (
                                                          treeReport.samples
                                                              ?.duplicate_ids_in_path ||
                                                          []
                                                      ).map(
                                                          (
                                                              id: number,
                                                              idx: number
                                                          ) => (
                                                              <button
                                                                  key={idx}
                                                                  onClick={() =>
                                                                      onOpenDetails(
                                                                          id
                                                                      )
                                                                  }
                                                                  className="text-blue-600 dark:text-blue-400 underline"
                                                              >
                                                                  {id}
                                                              </button>
                                                          )
                                                      )
                                                    : "-"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Модалка деталей по пользователю (users + user_tree) */}
            {isDetailsOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Пользователь и путь в дереве
                            </h3>
                            <button
                                onClick={onCloseDetails}
                                className="text-gray-600 hover:text-gray-800"
                            >
                                ✖️
                            </button>
                        </div>
                        {isLoadingTreeUser || !treeUserData ? (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                Загрузка...
                            </div>
                        ) : (
                            <div className="space-y-3 text-sm text-gray-900 dark:text-white">
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <div className="text-gray-500 dark:text-gray-400">
                                            ID
                                        </div>
                                        <div className="font-semibold">
                                            {treeUserData.data?.user?.id}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-gray-500 dark:text-gray-400">
                                            ФИО
                                        </div>
                                        <div className="font-semibold">
                                            {treeUserData.data?.user?.surname}{" "}
                                            {treeUserData.data?.user?.name}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-gray-500 dark:text-gray-400">
                                            Email
                                        </div>
                                        <div className="font-semibold break-all">
                                            {treeUserData.data?.user?.email ||
                                                "—"}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-gray-500 dark:text-gray-400">
                                            Inviter ID
                                        </div>
                                        <div className="font-semibold">
                                            {treeUserData.data?.user
                                                ?.inviter_id ?? "—"}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-gray-500 dark:text-gray-400">
                                            Заблокирован
                                        </div>
                                        <div className="font-semibold">
                                            {treeUserData.data?.user?.is_blocked
                                                ? "Да"
                                                : "Нет"}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-gray-500 dark:text-gray-400">
                                            Регистрация
                                        </div>
                                        <div className="font-semibold">
                                            {
                                                treeUserData.data?.user
                                                    ?.created_at
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div className="text-gray-500 dark:text-gray-400">
                                        Путь (path)
                                    </div>
                                    <div className="font-mono text-xs bg-gray-50 dark:bg-gray-900 p-2 rounded border border-gray-200 dark:border-gray-700 break-words">
                                        {Array.isArray(
                                            treeUserData.data?.path
                                        ) && treeUserData.data?.path.length
                                            ? treeUserData.data?.path.join(
                                                  " → "
                                              )
                                            : "—"}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-gray-500 dark:text-gray-400">
                                        Заблокированные в пути
                                    </div>
                                    <div className="font-mono text-xs bg-gray-50 dark:bg-gray-900 p-2 rounded border border-gray-200 dark:border-gray-700 break-words">
                                        {Array.isArray(
                                            treeUserData.data?.blocked_ancestors
                                        ) &&
                                        treeUserData.data?.blocked_ancestors
                                            .length
                                            ? treeUserData.data?.blocked_ancestors
                                                  .map(
                                                      (a: any) =>
                                                          `${a.id} (${
                                                              a.surname || ""
                                                          } ${a.name || ""})`
                                                  )
                                                  .join(", ")
                                            : "—"}
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={onCloseDetails}
                                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                            >
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
